/**
 * The shell: which screen is showing.
 *
 * Stage 2 covers the auth surfaces - loading, sign-in, and the self-serve
 * signup at /onboarding. Onboarding proper is stage 3 and the app shell is
 * stage 4; until then a signed-in session lands on a placeholder, because a
 * half-ported screen is worse than an honest one.
 *
 * `data-screen` is a deliberate test hook. The legacy page exposes which screen
 * is showing as `.screen.active` and the smoke suite reads it; React has no
 * equivalent, so the screens state their own identity. The names mirror the
 * legacy element ids on purpose, so one assertion reads both surfaces -
 * including `error-screen`, which in the old page is the SIGN-IN screen rather
 * than a fault.
 */
import { Toast } from "./components/Toast.tsx";
import { LoadingScreen } from "./screens/LoadingScreen.tsx";
import { SignInScreen } from "./screens/SignInScreen.tsx";
import { SignUpScreen } from "./screens/SignUpScreen.tsx";
import { SetPasswordScreen } from "./screens/SetPasswordScreen.tsx";
import { OnboardingScreen } from "./screens/OnboardingScreen.tsx";
import { AppScreen } from "./screens/AppScreen.tsx";
import { EditorScreen } from "./screens/EditorScreen.tsx";
import { NoBrandScreen } from "./screens/NoBrandScreen.tsx";
import { DemoScreen } from "./screens/demo/DemoScreen.tsx";
import { useBrand } from "./state/BrandContext.tsx";
import { shouldShowOnboarding } from "./screens/app/roleView.ts";

/**
 * Whether this URL is the self-serve signup entry point.
 *
 * `/onboarding` on the live site, `/next/onboarding` while the two apps run
 * side by side. Matched by suffix so the same build serves both, and the
 * cutover in stage 7 needs no change here.
 */
export function isSignupPath(pathname: string): boolean {
  return pathname === "/onboarding" || pathname.endsWith("/onboarding");
}

/**
 * Whether this URL is the one-time link from the set-a-password mail.
 *
 * Same suffix rule as `isSignupPath`, for the same reason. The visitor arriving
 * here has NO session - the branch below therefore has to run before the
 * signed-out one, or someone who clicked a link in their inbox lands on the
 * sign-in screen instead of the password field they were sent to.
 */
export function isSetPasswordPath(pathname: string): boolean {
  return pathname === "/set-password" || pathname.endsWith("/set-password");
}

/** The anonymous /demo flow: brief → showcase → price. Needs no session, so it wins over every session branch. */
export function isDemoPath(pathname: string): boolean {
  return pathname === "/demo" || pathname === "/demo/";
}

/**
 * The editor dashboard (step 1a). Its own surface, brand-independent: the
 * screen probes `/api/editor/me` and shows sign-in, not-an-editor, or the
 * dashboard. Suffix-matched like the signup and set-password paths so one
 * build serves `/editor` and `/next/editor`.
 */
export function isEditorPath(pathname: string): boolean {
  return pathname === "/editor" || pathname.endsWith("/editor");
}

/**
 * The editor per-card deep link (spec #93): `/editor/card/<id>`, or
 * `/next/editor/card/<id>` on the side-by-side build. Returns the id, or null
 * if this URL is not that route - same suffix convention as `isEditorPath`.
 */
export function editorCardIdFromPath(pathname: string): string | null {
  const id = /\/editor\/card\/([^/]+)\/?$/.exec(pathname)?.[1];
  return id ? decodeURIComponent(id) : null;
}

/**
 * The stable portal link to a card from its Trello short link:
 * `/editor/card/t/<shortLink>`. What a comment ping sends, since a ping only
 * knows the Trello card; `EditorScreen` resolves it to `/editor/card/<id>`.
 */
export function editorTrelloShortLinkFromPath(pathname: string): string | null {
  const link = /\/editor\/card\/t\/([^/]+)\/?$/.exec(pathname)?.[1];
  return link ? decodeURIComponent(link) : null;
}

export function isExpiredInvite(search: string): boolean {
  return new URLSearchParams(search).get("invite") === "expired";
}

export function App() {
  const { status, brand, error, notice, refresh } = useBrand();

  // A plain call, NOT a nested <Screen /> component. A component declared
  // inside App gets a new function identity on every render, so React would
  // treat it as a different type and remount its whole subtree - a toast
  // appearing would wipe the e-mail someone had half-typed into the sign-in
  // form.
  return (
    <>
      {screenFor()}
      <Toast notice={notice} />
    </>
  );

  function screenFor() {
    // Before "loading": the demo never waits on the session probe.
    if (isDemoPath(window.location.pathname)) return <DemoScreen />;
    // The editor dashboard (and its per-card deep link) is its own surface and
    // probes its own session, so it renders straight away without waiting on
    // the brand session probe.
    const trelloShortLink = editorTrelloShortLinkFromPath(window.location.pathname);
    if (trelloShortLink) return <EditorScreen trelloShortLink={trelloShortLink} />;
    const editorCardId = editorCardIdFromPath(window.location.pathname);
    if (editorCardId) return <EditorScreen cardId={editorCardId} />;
    if (isEditorPath(window.location.pathname)) return <EditorScreen />;
    if (status === "loading") return <LoadingScreen />;

    if (isExpiredInvite(window.location.search)) return <div className="shell" data-screen="invite-expired">
      <h1>This invitation is no longer available.</h1>
      <p>Ask the brand owner for a fresh invitation link. Your existing account and other brand access are unchanged.</p>
      <button className="btn btn-brand" onClick={() => {
        const url = new URL(window.location.href); url.searchParams.delete("invite");
        window.location.assign(url.toString());
      }}>Sign in</button>
    </div>;

    // Before every session branch: the address bar says what this visit is for,
    // and redeeming the link works whether or not a session is already open.
    if (isSetPasswordPath(window.location.pathname)) return <SetPasswordScreen />;

    // Signed out on /onboarding is not a dead end, it is the signup flow.
    if (status === "signed-out" && isSignupPath(window.location.pathname)) {
      return <SignUpScreen onRegistered={() => void refresh()} />;
    }

    if (status === "signed-out") return <SignInScreen />;

    // A signed-in person with no brand is either an editor or a client. The
    // editor dashboard probes `/api/editor/me`; anyone who is not an editor gets
    // the client's no-brand screen (create a brand, or ask to be invited) -
    // never "No editor access", which a client cannot act on (2026-10-08).
    if (status === "membership-required") return <EditorScreen notEditor={<NoBrandScreen onCreated={() => void refresh()} />} />;

    if (status === "error") {
      // A fault, not a refusal. Saying "please sign in" to someone who IS
      // signed in sends them after a cure for the wrong illness.
      return (
        <div className="shell" data-screen="fault-screen">
          <h1>Something went wrong</h1>
          <p>{error ?? "Could not reach the portal."}</p>
        </div>
      );
    }

    // Onboarding is not finished until the server says step 6. The live page
    // reads `onboardingSkipped` here too, but no endpoint has ever set it -
    // BrandStatusResponse does not carry the field, so the old check read
    // `undefined` every time. Left out rather than faithfully reproduced as a
    // no-op; if skipping is wanted it needs a server change and a decision.
    if (shouldShowOnboarding(brand?.onboardingStep ?? 0, brand?.role ?? "owner")) return <OnboardingScreen />;

    return <AppScreen key={`${brand?.self?.id ?? "brand-link"}:${brand?.id ?? "none"}`} />;
  }
}
